/** Segmented gauges and weight bars: every one is labelled and carries its number as text. */

import './chrome.css';
import { clamp, el } from '../dom';

export interface Gauge {
  readonly el: HTMLElement;
  set(value: number): void;
}

/** N boxes, the first `value` lit. `hotFrom` marks the segments that turn red (Readiness 6+). */
export function segmentGauge(label: string, segments: number, hotFrom = segments + 1): Gauge {
  const root = el('div', 'menu-gauge');
  const track = el('div', 'menu-gauge-track');
  const cells = Array.from({ length: segments }, (_, i) => {
    const cell = el('span', i + 1 >= hotFrom ? 'menu-gauge-seg menu-gauge-hot' : 'menu-gauge-seg');
    track.append(cell);
    return cell;
  });
  const number = el('span', 'menu-gauge-number');
  root.append(el('span', 'menu-gauge-label', label), track, number);
  root.setAttribute('role', 'img');
  return {
    el: root,
    set(value): void {
      const v = clamp(Math.round(value), 0, segments);
      cells.forEach((cell, i) => cell.classList.toggle('menu-gauge-on', i < v));
      number.textContent = `${v}/${segments}`;
      root.setAttribute('aria-label', `${label} ${v} OF ${segments}`);
    },
  };
}

export interface WeightBar {
  readonly el: HTMLElement;
  set(weight: number, cap: number): void;
}

/** A fill bar with `weight/cap` beside it; amber from 80 % of the cap, red at the cap. */
export function weightBar(): WeightBar {
  const root = el('div', 'menu-weight');
  const track = el('div', 'menu-weight-track');
  const fill = el('div', 'menu-weight-fill');
  track.append(fill);
  const number = el('span', 'menu-weight-number');
  root.append(track, number);
  return {
    el: root,
    set(weight, cap): void {
      const fraction = cap > 0 ? clamp(weight / cap, 0, 1) : 0;
      fill.style.width = `${Math.round(fraction * 100)}%`;
      fill.classList.toggle('menu-weight-high', fraction >= 0.8 && fraction < 1);
      fill.classList.toggle('menu-weight-full', fraction >= 1);
      number.textContent = `${weight}/${cap}`;
    },
  };
}

/** A plain progress bar (merit toward the next rank). */
export function progressBar(fraction: number): HTMLElement {
  const track = el('div', 'menu-progress');
  const fill = el('div', 'menu-progress-fill');
  fill.style.width = `${Math.round(clamp(fraction, 0, 1) * 100)}%`;
  track.append(fill);
  return track;
}
